import type { ReactNode } from 'react';
import { useState, useRef, Children } from 'react';
import loadable from '@loadable/component';
import {
  Wrapper,
  Summary,
  Content,
  Image,
  Description,
  WatchlistToggleWrapper,
  Slides,
  Slide,
  SlideButton,
} from './styled';
import {
  Grid,
  Button,
  useTheme,
  Text,
  Heading,
  Bones,
} from '@simplywallst/ui-core';
import ChevronLeftSolid from '@simplywallst/ui-core/icons/ChevronLeftSolid';
import ChevronRightSolid from '@simplywallst/ui-core/icons/ChevronRightSolid';
import Carousel from '@/components/Carousel';
import Observer from '@researchgate/react-intersection-observer';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import Media from 'react-media';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { useDelayedCallback } from '@/hooks/useDelayedCallback';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle'),
  { ssr: false }
);

const Modal = loadable(() => import('@simplywallst/ui-core/components/Modal'), {
  ssr: false,
});

const Snowflake = loadable(() => import('@simplywallst/ui-charts/Snowflake'), {
  resolveComponent: (components) => components.Snowflake,
  ssr: false,
});

const SnowflakeLabels = loadable(
  () => import('@simplywallst/ui-charts/Snowflake/components/Labels'),
  {
    resolveComponent: (components) => components.Labels,
    ssr: false,
  }
);

interface Props {
  index?: number;
  name?: string;
  image?: string;
  canonicalUrl?: string;
  companyId?: string;
  uniqueSymbol?: string;
  tickerSymbol?: string;
  description?: string;
  snowflakePoints?: [number, number, number, number, number];
  onLongHover?: (companyId: string) => void;
  children: ReactNode;
}

const HorizontallySlidingCompanyTile = ({
  index,
  children,
  name = '',
  canonicalUrl = '',
  image = '',
  companyId = '',
  uniqueSymbol = '',
  tickerSymbol = '',
  description = '',
  snowflakePoints = [0, 0, 0, 0, 0],
  onLongHover,
}: Props) => {
  const { isMobile, isTablet } = useUserAgent();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'HorizontallySlidingCompanyTile',
    index,
    uniqueSymbol,
  });
  const hasTrackedSlide = useRef(false);
  const theme = useTheme();
  const [showDescription, setShowDescription] = useState(false);
  const [isVisible, setIsVisible] = useState<boolean | undefined>();
  const [delayedCallback, cancelCallback] = useDelayedCallback(() => {
    if (typeof onLongHover !== 'undefined' && companyId.length > 0) {
      onLongHover(companyId);
    }
  });
  const desktopImage = useCloudflareImage({
    src: image,
    width: 350,
    height: 428,
    quality: 30,
  });
  const mobileImage = useCloudflareImage({
    src: image,
    width: 1024,
    height: 220,
    fit: 'cover',
    quality: 30,
  });
  const isLoading = companyId.length === 0;
  const childrenCount = Children.count(children);
  const columnsXl = Array.from({ length: childrenCount }, () => '372px').join(
    ' '
  );
  return (
    <Track>
      <Wrapper
        data-cy-id="discover-tile"
        onMouseOver={delayedCallback}
        onMouseOut={cancelCallback}
        onFocus={delayedCallback}
        onBlur={cancelCallback}
      >
        <Grid
          templateRows="270px 420px"
          templateRowsLg="428px"
          templateColumnsLg="350px auto"
        >
          <Observer
            threshold={[0, 0.75, 1]}
            onChange={(data) => {
              if (!isVisible && data.intersectionRatio > 0.75) {
                setIsVisible(true);
              }
            }}
          >
            <Summary>
              {isVisible && isLoading === false && (
                <Media
                  query={{ maxWidth: theme.mdMaxRaw }}
                  defaultMatches={isMobile || isTablet}
                >
                  {(matches) => (
                    <Image
                      src={matches ? mobileImage : desktopImage}
                      alt={name}
                    />
                  )}
                </Media>
              )}
              <Content
                templateColumns="130px auto"
                templateColumnsLg="1fr"
                templateRows="auto auto auto"
                rowGap="8px"
                columnGap="16px"
                rowGapLg="16px"
              >
                <Grid column="span 2" columnLg="1">
                  {isLoading ? (
                    <>
                      <Bones
                        marginTop="x0_5"
                        marginBottom="x1"
                        maxWidth="45%"
                      />
                      <Bones
                        marginTop="x0_5"
                        marginBottom="x1"
                        minHeight={24}
                      />
                    </>
                  ) : (
                    <RouterLink
                      noUnderline
                      to={canonicalUrl}
                      data-cy-id="summary-header-link"
                      onClick={() => {
                        trackEvent({
                          modifier: 'tile company name and ticker',
                          action: 'click',
                        });
                      }}
                    >
                      <Heading
                        as="h3"
                        typography="small"
                        color="whiteRGBA"
                        opacity={0.5}
                        lineClamp={1}
                      >
                        {uniqueSymbol}
                      </Heading>
                      <Heading as="h2" typography="xLargeStrong" lineClamp={1}>
                        {name}
                      </Heading>
                    </RouterLink>
                  )}
                </Grid>
                <Grid
                  templateRows="130px"
                  templateColumnsLg="166px"
                  templateRowsLg="166px"
                  padding="x0_5"
                  paddingLg="x0"
                >
                  {isVisible && isLoading === false ? (
                    <RouterLink to={canonicalUrl}>
                      <Snowflake points={snowflakePoints}>
                        <SnowflakeLabels />
                      </Snowflake>
                    </RouterLink>
                  ) : (
                    <Bones
                      borderRadius="x18"
                      minHeight={130}
                      minWidth={130}
                      maxHeight={166}
                      maxWidth={166}
                    />
                  )}
                </Grid>
                <Grid templateRows="130px" templateRowsLg="auto">
                  <Description alignContent="center">
                    {isLoading ? (
                      <>
                        <Bones marginTop="x0_5" marginBottom="x1" />
                        <Bones
                          marginTop="x0_5"
                          marginBottom="x1"
                          maxWidth="35%"
                        />
                      </>
                    ) : (
                      <Text typography="xSmall" color="white">
                        <span>{description}</span>
                        <Button
                          styleType="softLink"
                          onClick={() => {
                            trackEvent({
                              type: 'track',
                              modifier: 'show more',
                              action: 'click',
                            });
                            setShowDescription(true);
                          }}
                        >
                          Show more
                        </Button>
                      </Text>
                    )}
                    {showDescription && (
                      <Modal
                        title={uniqueSymbol}
                        onClose={() => setShowDescription(false)}
                        secondaryLabel="Close"
                        fullscreen={true}
                      >
                        {description}
                      </Modal>
                    )}
                  </Description>
                </Grid>
                <WatchlistToggleWrapper
                  data-cy-id="watchlist-toggle"
                  column="span 2"
                  columnLg="1"
                >
                  <WatchlistAddRemoveCompanyToggle
                    companyId={companyId}
                    tickerSymbol={tickerSymbol}
                    styleType="loudLarge"
                    display="block"
                    disabled={isLoading}
                    alignIcon={undefined}
                  />
                </WatchlistToggleWrapper>
              </Content>
            </Summary>
          </Observer>

          <Media
            query={{ maxWidth: theme.mdMaxRaw }}
            defaultMatches={isMobile || isTablet}
          >
            {(matches) => {
              return (
                <Carousel
                  showArrows={isLoading === false && matches === false}
                  slidesToScroll={matches ? 1 : 2}
                  align="start"
                  containScroll="trimSnaps"
                  draggable={isLoading === false}
                  dragFree={isLoading === false}
                  onScroll={() => {
                    if (hasTrackedSlide.current === false) {
                      trackEvent({
                        type: 'track',
                        modifier: 'slide',
                        action: 'scroll',
                      });
                      hasTrackedSlide.current = true;
                    }
                  }}
                  prevButton={
                    !matches ? (
                      <SlideButton>
                        <ChevronLeftSolid />
                      </SlideButton>
                    ) : undefined
                  }
                  nextButton={
                    !matches ? (
                      <SlideButton align="right">
                        <ChevronRightSolid />
                      </SlideButton>
                    ) : undefined
                  }
                >
                  <Slides
                    templateColumns={`repeat(${childrenCount}, 87%)`}
                    templateColumnsSm={`repeat(${childrenCount}, 47%)`}
                    templateColumnsXl={columnsXl}
                    templateRows="396px"
                    data-cy-id="slides"
                  >
                    {Children.map(children, (child, i) => {
                      return <Slide key={`sliding-tile-${i}`}>{child}</Slide>;
                    })}
                  </Slides>
                </Carousel>
              );
            }}
          </Media>
        </Grid>
      </Wrapper>
    </Track>
  );
};

export default HorizontallySlidingCompanyTile;
